import {
  ROTULO_ETAPA,
  ROTULO_SCORE,
  formatarReais,
  type EtapaFunil,
  type OrigemLead,
  type Score,
} from '@reducee/contracts';

import { useOrigens } from '../dominio/origens.ts';
import { Celula, Linha, Medalha, Pilula, Tabela, data } from './Superficie.tsx';

type VendaDaTabela = {
  id: string;
  produto: string;
  operadora: string | null;
  valorMensalCentavos: number;
  score: Score;
  etapa: EtapaFunil;
  origem: OrigemLead | null;
  campanha: string | null;
  criadaEm: string;
  primeiroBoletoPagoEm: string | null;
  contato: { id: string; nome: string };
  responsavel: { id: string; nome: string };
};

/**
 * Base comercial consolidada, uma linha por Oportunidade.
 *
 * A coluna do primeiro boleto é o pagamento que transforma a venda em
 * produção, e quem olha esta tabela está justamente conferindo quais vendas já
 * viraram produção. O dado é da implantação, mas chega junto da Oportunidade:
 * a Implantação é tela da Administradora, e a Comercial também usa esta lista.
 */
export function TabelaDeVendas({
  oportunidades,
  aoSelecionar,
}: {
  oportunidades: VendaDaTabela[];
  aoSelecionar: (id: string) => void;
}): React.JSX.Element {
  const origens = useOrigens();
  const ordenadas = [...oportunidades].sort((uma, outra) =>
    outra.criadaEm.localeCompare(uma.criadaEm),
  );

  return (
    <Tabela
      teste="tabela-vendas"
      colunas={[
        'Data',
        'Cliente',
        'Score',
        'Responsável',
        'Operadora',
        'Produto',
        'Valor',
        'Etapa',
        '1º boleto',
        'Origem',
        'Ação',
      ]}
      vazio="Nenhuma oportunidade registrada."
    >
      {ordenadas.map((venda) => {
        const pagoEm = venda.primeiroBoletoPagoEm;

        return (
          <Linha key={venda.id}>
            <Celula>{data(venda.criadaEm)}</Celula>
            <Celula>
              <strong>{venda.contato.nome}</strong>
            </Celula>
            <Celula>
              <Medalha score={venda.score}>{ROTULO_SCORE[venda.score]}</Medalha>
            </Celula>
            <Celula>{venda.responsavel.nome}</Celula>
            <Celula>{venda.operadora ?? '—'}</Celula>
            <Celula>{venda.produto}</Celula>
            <Celula>{formatarReais(venda.valorMensalCentavos)}</Celula>
            <Celula>
              <Pilula>{ROTULO_ETAPA[venda.etapa]}</Pilula>
            </Celula>
            <Celula>
              {pagoEm ? (
                <Pilula tom="sucesso">Pago {data(pagoEm)}</Pilula>
              ) : (
                <Pilula tom="dourado">Pendente</Pilula>
              )}
            </Celula>
            <Celula className="whitespace-nowrap">
              {(() => {
                const origem = venda.origem ? origens.nome(venda.origem) : '—';
                // Campanha só acrescenta quando diz algo além da origem:
                // "Reducee · Reducee" é ruído.
                const campanha = venda.campanha?.trim();
                return campanha && campanha !== origem ? `${origem} · ${campanha}` : origem;
              })()}
            </Celula>
            <Celula>
              <button
                type="button"
                onClick={() => aoSelecionar(venda.id)}
                className="rounded-lg border border-linha px-2 py-1.5 text-[10px] font-bold transition hover:bg-marinho-suave"
              >
                Editar
              </button>
            </Celula>
          </Linha>
        );
      })}
    </Tabela>
  );
}
